import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getBaseUrl } from "../../Redux/Config/AppConfig";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const ManageHotel = () => {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmType, setConfirmType] = useState(""); // 'edit' or 'delete'
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    address: "",
    url: "",
    email: "",
    password: "",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [originalPassword, setOriginalPassword] = useState("");

  const baseUrl = getBaseUrl();

  const fetchHotels = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/hotel/all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && Array.isArray(data.data)) {
        setHotels(data.data);
      } else {
        setError(data?.message || "Failed to fetch hotels");
      }
    } catch (err) {
      setError("Error fetching hotels");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHotels();
  }, []);

  const handleEditClick = (hotel) => {
    console.log(hotel);
    setSelectedHotel(hotel);
    const adminPassword = hotel.admin.password || "";
    setEditForm({
      name: hotel.name,
      address: hotel.address,
      url: hotel.url || "",
      email: hotel.admin.email || "",
      password: adminPassword,
    });
    setOriginalPassword(adminPassword);
    setConfirmType("edit");
    setShowConfirmModal(true);
  };

  const handleDeleteClick = (hotel) => {
    setSelectedHotel(hotel);
    setConfirmType("delete");
    setShowConfirmModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async () => {
    setEditLoading(true);
    try {
      const token = localStorage.getItem("token");

      // Prepare request body
      const requestBody = {
        name: editForm.name,
        address: editForm.address,
        url: editForm.url || "",
        email: editForm.email || "",
      };

      // Only include password if it has changed
      if (
        editForm.password !== originalPassword &&
        editForm.password !== "" &&
        editForm.password !== null
        ) {
        requestBody.password = editForm.password;
        console.log("Password changed, will update");
      } else {
        console.log("Password unchanged, skipping password update");
      }

      const res = await fetch(`${baseUrl}/hotel/${selectedHotel.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Hotel updated successfully");
        setShowConfirmModal(false);
        fetchHotels();
      } else {
        toast.error(data?.message || "Failed to update hotel");
      }
    } catch (err) {
      toast.error("Error updating hotel");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteSubmit = async () => {
    setEditLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/hotel/${selectedHotel.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Hotel deleted successfully");
        setShowConfirmModal(false);
        fetchHotels();
      } else {
        toast.error(data?.message || "Failed to delete hotel");
      }
    } catch (err) {
      toast.error("Error deleting hotel");
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="container py-4">
      <h2 className="mb-4">Manage Hotels</h2>
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : hotels.length === 0 ? (
        <div className="alert alert-info">No hotels found.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>Owner</th>
                <th>Owner Email</th>
                <th style={{ width: 160 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {hotels.map((hotel) => (
                <tr key={hotel.id}>
                  <td>{hotel.name}</td>
                  <td>{hotel.address}</td>
                  <td>
                    {hotel.admin
                      ? `${hotel.admin.first_name} ${hotel.admin.last_name}`
                      : ""}
                  </td>
                  <td>
                    {hotel.admin ? hotel.admin.email : ""}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-primary me-2"
                      onClick={() => handleEditClick(hotel)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteClick(hotel)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {confirmType === "edit" ? "Edit Hotel" : "Delete Hotel"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowConfirmModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {confirmType === "edit" ? (
                  <>
                    <div className="mb-3">
                      <label className="form-label">Hotel Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={editForm.name}
                        onChange={handleEditChange}
                        disabled={editLoading}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Address</label>
                      <input
                        type="text"
                        className="form-control"
                        name="address"
                        value={editForm.address}
                        onChange={handleEditChange}
                        disabled={editLoading}
                      />
                    </div>
                    <div className="mb-3">
                      {" "}
                      {/* New URL input field */}
                      <label className="form-label">URL</label>
                      <input
                        type="text"
                        className="form-control"
                        name="url"
                        value={editForm.url}
                        onChange={handleEditChange}
                        disabled={editLoading}
                        autoComplete="off"
                        placeholder="https://www.example.com"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Admin Email</label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={editForm.email}
                        onChange={handleEditChange}
                        disabled={true}
                        placeholder="hotel.admin@example.com"
                      />
                      <small className="form-text text-muted">
                        Email address for the hotel admin account
                      </small>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Admin Password
                        {editForm.password !== originalPassword && (
                          <span className="badge bg-warning text-dark ms-2">
                            Modified
                          </span>
                        )}
                      </label>
                      <div className="input-group">
                        <input
                          type={showPassword ? "text" : "password"}
                          className={`form-control ${
                            editForm.password !== originalPassword
                              ? "border-warning"
                              : ""
                          }`}
                          name="password"
                          value={editForm.password}
                          onChange={handleEditChange}
                          disabled={editLoading}
                          placeholder="Leave blank to keep current password"
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={editLoading}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      <small className="form-text text-muted">
                        Password for the hotel admin account (encrypted in
                        database)
                      </small>
                      {editForm.password !== originalPassword && (
                        <div className="alert alert-warning mt-2">
                          <small>
                            <strong>Password will be updated:</strong> The
                            password field has been modified and will be updated
                            when you save.
                          </small>
                        </div>
                      )}
                      <div className="alert alert-info mt-2">
                        <small>
                          <strong>Note:</strong> The password shown above is a
                          bcrypt hash (one-way encrypted). For security reasons,
                          passwords cannot be decrypted or viewed. To change the
                          password, enter a new one in the field above.
                        </small>
                      </div>
                    </div>
                  </>
                ) : (
                  <div>
                    Are you sure you want to delete hotel{" "}
                    <strong>{selectedHotel?.name}</strong>?
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowConfirmModal(false)}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                {confirmType === "edit" ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleEditSubmit}
                    disabled={editLoading}
                  >
                    {editLoading ? "Saving..." : "Save Changes"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteSubmit}
                    disabled={editLoading}
                  >
                    {editLoading ? "Deleting..." : "Delete"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageHotel;
